// Admin: one-time milestone email blast to Credit Union membership pledgers.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Mail, Send, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const errMsg = (e) => formatApiError(e.response?.data?.detail);

function Stat({ label, value, testId }) {
  return (
    <div className="border border-bdu-gold/15 px-4 py-3" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-[0.24em] text-bdu-cream/50">{label}</div>
      <div className="mt-1 font-serif text-2xl text-bdu-cream tabular-nums">{value}</div>
    </div>
  );
}

function JobStatus({ job }) {
  if (!job) return null;
  const when = new Date(job.finished_at || job.started_at).toLocaleString();
  const text = {
    running: `Sending now… ${job.sent} of ${job.total} sent${job.failed ? ` · ${job.failed} failed` : ""}`,
    done: `Last send finished ${when} · ${job.sent} sent${job.failed ? ` · ${job.failed} failed` : ""}`,
    interrupted: `Last send stopped early (${job.sent} of ${job.total} sent). Press Send to reach the rest.`,
  }[job.status];
  return <p className={`text-xs ${job.status === "running" ? "text-bdu-gold" : "text-bdu-cream/60"}`} data-testid="admin-milestone-email-job">{text}</p>;
}

function PreviewModal({ html, onClose }) {
  return (
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-milestone-email-preview-modal">
      <div className="w-full max-w-2xl h-[85vh] bg-bdu-bg border border-bdu-gold/30 flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-bdu-gold/15">
          <span className="label-eyebrow">Email preview</span>
          <button onClick={onClose} className="text-bdu-cream/60 hover:text-bdu-gold" data-testid="admin-milestone-email-preview-close"><X size={18} /></button>
        </div>
        <iframe title="Milestone email preview" srcDoc={html} sandbox="" className="flex-1 w-full bg-white" />
      </div>
    </div>
  );
}

export default function MilestoneEmailCard() {
  const [info, setInfo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/milestone/email").then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const running = info?.job?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [running, load]);

  const openPreview = () => api.get("/admin/milestone/email/preview", { responseType: "text" })
    .then((r) => setPreview(r.data)).catch((e) => toast.error(errMsg(e)));

  const send = async () => {
    if (!window.confirm(`Send "${info.subject}" to ${info.remaining} pledger${info.remaining === 1 ? "" : "s"}?\n\nThese are real emails and can't be unsent.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/milestone/email/send");
      toast.success(`Sending to ${r.data.total} pledgers`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const blocked = !info || busy || running || !info.letter_posted || info.remaining === 0;
  return (
    <section className="border border-bdu-gold/25 bg-bdu-surface2 p-6 space-y-5" data-testid="admin-milestone-email">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <Mail size={22} className="text-bdu-gold shrink-0 mt-1" />
          <div className="min-w-0">
            <div className="label-eyebrow">Milestone email blast · membership pledgers</div>
            <div className="mt-1 font-serif text-lg text-bdu-cream" data-testid="admin-milestone-email-subject">{info?.subject || "Loading…"}</div>
            <p className="mt-1 text-xs text-bdu-cream/55">Each pledger gets it once. Pressing Send again only reaches people who pledged since the last send.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={openPreview} className="btn-ghost text-xs inline-flex items-center gap-2" data-testid="admin-milestone-email-preview-btn"><Eye size={14} /> Preview</button>
          <button onClick={send} disabled={blocked} className="btn-gold text-xs disabled:opacity-40 disabled:cursor-not-allowed" data-testid="admin-milestone-email-send-btn">
            <Send size={14} /> {running ? "Sending…" : `Send to ${info?.remaining ?? 0}`}
          </button>
        </div>
      </div>
      {info && (
        <div className="grid grid-cols-3 gap-3 max-w-lg">
          <Stat label="Pledgers with email" value={info.recipients} testId="admin-milestone-email-recipients" />
          <Stat label="Already sent" value={info.already_sent} testId="admin-milestone-email-already" />
          <Stat label="Will receive" value={info.remaining} testId="admin-milestone-email-remaining" />
        </div>
      )}
      {info && !info.letter_posted && <p className="text-xs text-amber-400" data-testid="admin-milestone-email-letter-warning">Post the FOM letter above first. The email links to it.</p>}
      <JobStatus job={info?.job} />
      {preview && <PreviewModal html={preview} onClose={() => setPreview(null)} />}
    </section>
  );
}
